import React from "react";
import { GiWeight } from "react-icons/gi";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { getShipmozoUrl } from "./apiUrls";

export default function RateCard({
  rate,
  lspObject,
  packageType,
  pickupPincode,
  deliveryPincode,
  boxes,
  expanded,
  onToggleExpand,
  index,
  insuranceOption,
  paymentMode,
  codAmount,
  invoiceValue,
  selfDropOption,
  showAdvanced,
}) {
  // Function to handle WhatsApp click with URL parameters
  const handleWhatsAppClick = () => {
    // Create the paramsData object for URL parameters
    const paramsData = {
      originPincode: pickupPincode,
      destinationPincode: deliveryPincode,
      serviceCategory: packageType.toLowerCase(),
      riskType: insuranceOption,
      isGSTinclusiv: false,
      isCOD: paymentMode === "COD",
      selfDrop: selfDropOption === "Self-drop",
      codAmount: paymentMode === "COD" ? Number(codAmount) : 0,
      weightDetailsArray: boxes.map((box) => {
        return {
          weightKg: box.Weightperbox,
          lengthCm: box.length,
          breadthCm: box.width,
          heightCm: box.height,
          quantity: showAdvanced ? box.noOfBoxes : 1,
        };
      }),
      ...(insuranceOption === "carrierRisk" && invoiceValue
        ? { invoiceValue: Number(invoiceValue) }
        : {}),
    };

    // Create the URL using the shipmozourl from apiUrls
    const url = getShipmozoUrl(paramsData);

    // Open the URL in a new tab
    window.open(url, "_blank");
  };
  return (
    <div className="border border-indigo-200 rounded-xl shadow-sm overflow-hidden mb-5 w-full bg-white">
      {/* Main Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 px-4 py-4 sm:py-3 items-center text-sm text-gray-800 min-h-[80px]">
        {/* Logo + Company Name */}
        <div className="flex items-center gap-2">
          <img
            src={lspObject?.lspImagePath || "/image/indianpost (1).png"}
            alt={lspObject?.companyName || "Courier"}
            className="h-6 w-auto shrink-0"
            onError={(e) => {
              console.log("Image failed to load:", e.target.src);
              // Fallback to IndiaPost image if the original image fails to load
              if (e.target.src !== "/image/indianpost (1).png") {
                e.target.src = "/image/indianpost (1).png";
              }
            }}
          />
        </div>

        {/* Package Type & Service */}
        <div className="text-blue-900 font-semibold text-xs sm:text-center">
          {packageType.toUpperCase()} <br />
          <span className="text-gray-500 font-normal">{rate.service}</span>
        </div>

        {/* TAT */}
        <div className="text-indigo-600 text-sm sm:text-center">
          {rate.tat} {rate.tat === 1 ? "Day" : "Days"}
        </div>

        {/* Weight */}
        <div className="flex sm:justify-center items-center text-gray-700 gap-1">
          {rate.finalChargeableWeight > 0 && (
            <>
              <GiWeight className="text-base text-gray-500" />
              {rate.finalChargeableWeight} kg
            </>
          )}
        </div>

        {/* Price */}
        <div className="text-blue-900 font-extrabold text-xl sm:text-center">
          ₹{rate.rateSummary?.total}
        </div>

        {/* WhatsApp */}
        <div className="text-center sm:text-right">
          <button
            onClick={handleWhatsAppClick}
            className="inline-flex items-center gap-1 text-white bg-blue-950 rounded-md px-3 py-1.5 text-xs cursor-pointer hover:bg-blue-900 transition-colors"
          >
         
            Book Now
          </button>
        </div>
      </div>

      {/* Toggle Button */}
      <div className="border-t px-4 py-2 bg-gray-50">
        <button
          onClick={() => onToggleExpand(index)}
          className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-800"
        >
          {expanded ? "Hide freight breakup" : "Show freight breakup"}
          {expanded ? (
            <IoChevronUp className="w-4 h-4" />
          ) : (
            <IoChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Expandable Freight Breakup Table */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          expanded ? "max-h-96 opacity-100 px-4 pb-3" : "max-h-0 opacity-0 px-4"
        } overflow-hidden`}
      >
        <div className="bg-white rounded-md border border-gray-200 mt-2 overflow-hidden">
          <table className="w-full text-xs">
            <tbody>
              {[
                rate.rateSummary?.tsAmount > 0 && {
                  label: "Base Cost",
                  value: `₹${rate.rateSummary.tsAmount}`,
                },
                rate.rateSummary?.codCharge > 0 && {
                  label: "COD Charge",
                  value: `₹${rate.rateSummary.codCharge}`,
                },
                rate.rateSummary?.fsc > 0 && {
                  label: "Fuel Surcharge",
                  value: `₹${rate.rateSummary.fsc}`,
                },
                rate.rateSummary?.rtoCharge > 0 && {
                  label: "RTO Charge",
                  value: `₹${rate.rateSummary.rtoCharge}`,
                },
                rate.rateSummary?.docketCharge > 0 && {
                  label: "Docket Charge",
                  value: `₹${rate.rateSummary.docketCharge}`,
                },
                rate.rateSummary?.firstMileCharge > 0 && {
                  label: "First Mile Charge",
                  value: `₹${rate.rateSummary.firstMileCharge}`,
                },
                rate.rateSummary?.riskSurcharge > 0 && {
                  label: "Risk Surcharge",
                  value: `₹${rate.rateSummary.riskSurcharge}`,
                },
                rate.rateSummary?.toPayCharge > 0 && {
                  label: "To-Pay Charge",
                  value: `₹${rate.rateSummary.toPayCharge}`,
                },
                rate.rateSummary?.odaCharge > 0 && {
                  label: "ODA Charge",
                  value: `₹${rate.rateSummary.odaCharge}`,
                },
                rate.rateSummary?.handlingCharge > 0 && {
                  label: "Handling Charge",
                  value: `₹${rate.rateSummary.handlingCharge}`,
                },
                rate.rateSummary?.greenCharge > 0 && {
                  label: "Green Charge",
                  value: `₹${rate.rateSummary.greenCharge}`,
                },
                rate.rateSummary?.gst > 0 && {
                  label: "GST",
                  value: `₹${rate.rateSummary.gst}`,
                },
              ]
                .filter(Boolean)
                .map((item, idx) => (
                  <tr key={idx} className=" last:border-none text-gray-700">
                    <td className="py-1 px-3">{item.label}</td>
                    <td className="py-1 px-3 text-right font-medium">
                      {item.value}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {rate?.error && !rate.success && rate.provider !== "delhivery" && (
          <div className="mt-2 text-xs text-red-600">{rate.error}</div>
        )}
      </div>
    </div>
  );
}







